import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle, faWarning } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { HexColor } from '@sorare/core/src/style/types';

import { TargetIcon } from 'assets/icons/TargetIcon';
import { LineupRewardsDistances } from 'components/lineup/LineupRewardsDistances';
import { So5RankingRewards } from 'components/ranking/So5RankingRewards';
import { RewardMultiplier } from 'components/rewards/AnyRewardConfig/RewardMultiplier';
import { Variant } from 'components/rewards/RewardOverview/types';
import {
  ContenderLeftFooterState,
  getContenderLeftFooterState,
} from 'lib/contender';

import type { LeftLiveLineupFooter_so5LeaderboardContender } from './__generated__/index.graphql';

const messages = defineMessages({
  scoreToTarget: {
    id: 'Lineup.LiveLineupFooter.scoreToTarget',
    defaultMessage: '<b>{score}</b> to target',
  },
  scoreToClosestTarget: {
    id: 'Lineup.LiveLineupFooter.RightLiveLineupFooter.scoreToClosestTarget',
    defaultMessage: '<b>{score}</b> to closest target',
  },
  scoreToClosestReward: {
    id: 'Lineup.LiveLineupFooter.scoreToClosestReward',
    defaultMessage: '<b>{score}</b> to closest reward',
  },
});

const StyledBold = styled.b`
  color: var(--c-white);
`;

type Props = {
  so5LeaderboardContender: LeftLiveLineupFooter_so5LeaderboardContender;
};

export const LeftLiveLineupFooter = ({ so5LeaderboardContender }: Props) => {
  const { so5Lineup } = so5LeaderboardContender;
  const so5Ranking = so5Lineup?.so5Rankings.at(0);
  const leftState = getContenderLeftFooterState(so5LeaderboardContender);
  if (leftState === null || !so5Ranking) {
    return null;
  }

  const { state } = leftState;

  const lineupDistances = so5Ranking && (
    <Dropdown
      horizontalGap={-8}
      label={props => (
        <IconButton {...props} icon={faInfoCircle} color="transparent" small />
      )}
    >
      {({ closeDropdown }) => (
        <LineupRewardsDistances
          so5RankingId={idFromObject(so5Ranking.id)}
          onClose={closeDropdown}
        />
      )}
    </Dropdown>
  );

  let icon: ReactNode = null;
  let content: ReactNode = null;

  switch (state) {
    case ContenderLeftFooterState.CANCELLED:
      icon = <FontAwesomeIcon icon={faWarning} color="var(--c-yellow-400)" />;
      content = (
        <Ellipsis>
          <LabelM>{so5Lineup?.cancelledReason}</LabelM>
        </Ellipsis>
      );
      break;
    case ContenderLeftFooterState.NOT_STARTED:
      icon = lineupDistances;
      content = (
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage
            id="Lineup.LiveLineupFooter.timeLeftToNextMatch"
            defaultMessage="<b>{timeLeft}</b> to next match"
            values={{
              b: (chunks: ReactNode[]) => {
                return <StyledBold>{chunks}</StyledBold>;
              },
              timeLeft: <TimeLeft time={leftState.date} />,
            }}
          />
        </LabelM>
      );
      break;
    case ContenderLeftFooterState.REWARDS:
      icon = lineupDistances;
      content = so5Ranking && (
        <LinkOther as="div">
          <Horizontal gap={0.5}>
            <So5RankingRewards
              variant={Variant.INLINE}
              so5Ranking={so5Ranking}
            />
            {so5Lineup && <RewardMultiplier lineup={so5Lineup} />}
          </Horizontal>
        </LinkOther>
      );
      break;
    case ContenderLeftFooterState.POINTS_TO_CLOSEST_REWARD:
      icon = lineupDistances;
      content = (
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage
            {...messages.scoreToClosestReward}
            values={{
              b: (chunks: ReactNode[]) => {
                return <StyledBold>{chunks}</StyledBold>;
              },
              score: (
                <FormattedMessage
                  {...fantasy.pointsWithoutFormat}
                  values={{
                    score: leftState.points,
                  }}
                />
              ),
            }}
          />
        </LabelM>
      );
      break;
    case ContenderLeftFooterState.POINTS_TO_SAFETY:
      icon = lineupDistances;
      content = (
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage
            id="Lineup.LiveLineupFooter.scoreToSafety"
            defaultMessage="<b>{score}</b> to safety"
            values={{
              b: (chunks: ReactNode[]) => {
                return <StyledBold>{chunks}</StyledBold>;
              },
              score: (
                <FormattedMessage
                  {...fantasy.pointsWithoutFormat}
                  values={{
                    b: StyledBold,
                    score: leftState.points,
                  }}
                />
              ),
            }}
          />
        </LabelM>
      );
      break;
    case ContenderLeftFooterState.POINTS_TO_TARGET:
    case ContenderLeftFooterState.POINTS_TO_THRESHOLD:
      if (state === ContenderLeftFooterState.POINTS_TO_TARGET) {
        icon = <img src={leftState.iconUrl} alt="" height={12} />;
      } else if (state === ContenderLeftFooterState.POINTS_TO_THRESHOLD) {
        icon = (
          <TargetIcon
            color={`rgb(${leftState.color})` as HexColor}
            size={1.5}
          />
        );
      }
      content = (
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage
            {...(state === ContenderLeftFooterState.POINTS_TO_TARGET
              ? messages.scoreToTarget
              : messages.scoreToClosestTarget)}
            values={{
              b: (chunks: ReactNode[]) => {
                return <StyledBold>{chunks}</StyledBold>;
              },
              score: (
                <FormattedMessage
                  {...fantasy.pointsWithoutFormat}
                  values={{
                    b: StyledBold,
                    score: leftState.points,
                  }}
                />
              ),
            }}
          />
        </LabelM>
      );
      break;
    default:
      return null;
  }

  return (
    <Horizontal gap={0.5}>
      {icon}
      {content}
    </Horizontal>
  );
};

LeftLiveLineupFooter.fragments = {
  so5LeaderboardContender: gql`
    fragment LeftLiveLineupFooter_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        rewardMultiplier
        cancelledReason
        so5Rankings {
          id
          ...So5RankingRewards_so5Ranking
        }
        ...RewardMultiplier_lineup
      }
      ...getContenderLeftFooterState_so5LeaderboardContender
    }
    ${getContenderLeftFooterState.fragments.so5LeaderboardContender}
    ${So5RankingRewards.fragments.so5Ranking}
    ${RewardMultiplier.fragments.lineup}
  ` as TypedDocumentNode<LeftLiveLineupFooter_so5LeaderboardContender>,
};
